import React, { useRef, useState, useEffect } from 'react';
import { 
  Download, 
  Printer, 
  Copy, 
  Check, 
  ShieldCheck, 
  Sparkles, 
  FileText, 
  TrendingUp, 
  Lock, 
  Layers, 
  DollarSign, 
  Users, 
  Building2, 
  ExternalLink,
  Award,
  Globe,
  CheckCircle2,
  Calendar,
  Car,
  Sliders,
  Mail,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Save,
  CheckCircle,
  FileCheck
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

interface DealershipProposalViewProps {
  showToast?: (msg: string, type?: 'success' | 'info' | 'warning' | 'error') => void;
}

const STORAGE_KEY = 'azuraos_dealership_proposal_params_v2';

export function DealershipProposalView({ showToast }: DealershipProposalViewProps) {
  const [activeTab, setActiveTab] = useState<'preview' | 'code_block' | 'email' | 'customize'>('preview');
  const [documentTheme, setDocumentTheme] = useState<'dark' | 'light'>('light');
  const [copiedProposal, setCopiedProposal] = useState(false);
  const [copiedCodeBlock, setCopiedCodeBlock] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Form State with Local Storage Persistence
  const [dealershipName, setDealershipName] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.dealershipName) return parsed.dealershipName;
      }
    } catch {}
    return 'Eagle Ford Motor Company';
  });

  const [financialManagerName, setFinancialManagerName] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.financialManagerName) return parsed.financialManagerName;
      }
    } catch {}
    return 'Karisma Singh-Jailall';
  });

  const [financialManagerTitle, setFinancialManagerTitle] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.financialManagerTitle) return parsed.financialManagerTitle;
      }
    } catch {}
    return 'Financial Manager';
  });

  const [dealerPrincipalName, setDealerPrincipalName] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.dealerPrincipalName) return parsed.dealerPrincipalName;
      }
    } catch {}
    return 'Craig Gillesen';
  });

  const [dealerPrincipalTitle, setDealerPrincipalTitle] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.dealerPrincipalTitle) return parsed.dealerPrincipalTitle;
      }
    } catch {}
    return 'Dealer Principal';
  });

  const [companyProvider, setCompanyProvider] = useState('Azura Seraph™ Pty Ltd');
  const [companyRegNumber, setCompanyRegNumber] = useState('2024/560516/07');
  const [cityLocation, setCityLocation] = useState('Johannesburg, Gauteng');
  const [banksList, setBanksList] = useState('Nedbank, FNB, Standard Bank, Absa');
  const [financeHouses, setFinanceHouses] = useState('WesBank, MFC, Absa Vehicle Finance, Standard Bank');
  const [pilotDays, setPilotDays] = useState('14-Day Parallel Run');
  const [salarySavings, setSalarySavings] = useState('R20,000 – R35,000 / month');
  const [postPilotFee, setPostPilotFee] = useState('R3,500 – R5,000 / month (vs R25,000/mo salary)');
  const [pilotRef, setPilotRef] = useState('AZ-PILOT-EAGLEFORD-2026');

  // Auto-persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        dealershipName,
        financialManagerName,
        financialManagerTitle,
        dealerPrincipalName,
        dealerPrincipalTitle,
        companyProvider,
        companyRegNumber,
        cityLocation,
        banksList,
        financeHouses,
        pilotDays,
        salarySavings,
        postPilotFee,
        pilotRef
      }));
    } catch {}
  }, [
    dealershipName,
    financialManagerName,
    financialManagerTitle,
    dealerPrincipalName,
    dealerPrincipalTitle,
    companyProvider,
    companyRegNumber,
    cityLocation,
    banksList,
    financeHouses,
    pilotDays,
    salarySavings,
    postPilotFee,
    pilotRef
  ]);

  const proposalDocRef = useRef<HTMLDivElement>(null);

  // Plain Text Version of Proposal for Instant Copy
  const plainTextProposal = `================================================================================
AZURAOS™ ENTERPRISE PILOT PROPOSAL & SLA: EAGLE FORD MOTOR COMPANY
================================================================================
TO:         ${financialManagerName} (${financialManagerTitle})
            ${dealerPrincipalName} (${dealerPrincipalTitle})
COMPANY:    ${dealershipName} (${cityLocation})
FROM:       Raksha Singh, Principal Architect — ${companyProvider}
            (Official Google Cloud Partner Advantage Member)
REG NO:     ${companyRegNumber}
REF NO:     ${pilotRef}
DATE:       ${new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
PILOT TYPE: ${pilotDays} (Complimentary Beta Trial)
================================================================================

1. EXECUTIVE OVERVIEW: THE COST OF MANUAL DEALERSHIP RECONCILIATION
In automotive retail, financial managers spend 15 to 20 hours per week 
on high-stress manual bank reconciliation:
• Matching customer cash and EFT deposits to individual vehicle stock numbers.
• Reconciling vehicle asset finance payouts from banks (${financeHouses}).
• Tracking floorplan interest erosion on aging showroom inventory.
• Calculating moving 15% VAT liabilities on second-hand vehicle gross profits 
  ahead of bi-monthly SARS VAT201 deadlines.

THE HEADCOUNT BOTTLENECK:
Hiring a junior assistant accountant costs ${salarySavings} in fixed payroll, 
accompanied by recruitment friction, training turnover, and supervision drag.

--------------------------------------------------------------------------------
2. THE AZURAOS PILOT SOLUTION
AzuraOS functions as an air-gapped digital accounting assistant that eliminates 
manual data entry while keeping Financial Management in 100% control:

A. 0.05-SECOND AIR-GAPPED MULTI-CHANNEL BANK RECONCILIATION
   • Drag-and-drop daily ${banksList} PDF/CSV statements.
   • Automatically identifies vehicle stock numbers, customer cash deposits, 
     and bank asset finance settlements in under 50 milliseconds.

B. 1-CLICK HUMAN-IN-THE-LOOP CONTROL
   • No blind auto-posting. The Financial Manager reviews, adjusts, and 
     approves line items with a single click before final sign-off.

C. SARS 15% VAT & PROVISIONAL TAX HOLDING SHIELD
   • Automatically ring-fences indicative 15% VAT buffers on vehicle margins 
     into dedicated virtual reserve allocations, eliminating tax surprises.

D. BANK-GRADE AIR-GAPPED PRIVACY (ZERO PASSWORD SHARING)
   • 100% local-first Web Cryptography AES-256 architecture running in-browser.
   • Zero third-party credential scraping (Anti-Plaid/Anti-Yodlee). Your online 
     banking logins and customer records never leave your local computer.

--------------------------------------------------------------------------------
3. SCOPE OF THE 14-DAY PARALLEL PILOT
• DURATION:     ${pilotDays} (Runs in parallel with your existing accounting workflow)
• COMMITMENT:   R0 (Complimentary Beta Pilot for ${dealershipName})
• SETUP TIME:   Under 15 minutes (Web-based; zero IT or server installation required)
• DELIVERABLES:
  1. Dedicated air-gapped local vault setup on local office PC.
  2. Multi-channel bank statement reconciliation engine mapping.
  3. Real-time SARS 15% VAT margin defense calculator.
  4. 1-click human-in-the-loop executive cash flow reporting.

--------------------------------------------------------------------------------
4. COMMERCIAL VALUE & ECONOMICS
• Current Assistant Accountant Payroll:  ${salarySavings}
• Post-Pilot AzuraOS Retainer:           ${postPilotFee}
• Direct Monthly Net Cash Savings:       R16,500 – R30,000 / month
• Annual Bottom-Line Margin Protection:  R198,000 – R360,000 / year

--------------------------------------------------------------------------------
5. CONFIDENTIALITY & DATA OWNERSHIP COVENANT
${companyProvider} (Reg: ${companyRegNumber}) provides a zero-custody 
guarantee. All transactional data, bank statements, and client vehicle deals 
remain strictly resident in the dealership's local browser vault. No financial 
records are stored on public servers or used for artificial intelligence training.
================================================================================`;

  // Sister Email Draft for Instant Copy
  const sisterEmailDraft = `Subject: Quick thought on your assistant accountant search + a 14-day Eagle Ford pilot proposal

Hi Karisma,

I hope Joburg is treating you well!

I was thinking about our last chat and how much of a headache it’s been trying to hire and keep a reliable assistant accountant at Eagle Ford. Between matching daily floor deposits, customer EFTs, bank payouts from WesBank/Absa/MFC, and keeping up with SARS VAT buffers, I know how brutal that daily manual recon grind is for you.

As you know, through ${companyProvider} (Registration Number: ${companyRegNumber} and our Google Cloud Partner build), we’ve been running and testing AzuraOS—our air-gapped financial engine.

Instead of you having to spend weeks interviewing, training, and babysitting another junior assistant who might leave in three months, I’ve put together a brief proposal for a no-cost 14-day internal pilot for ${dealershipName}.

It’s built with strict human-in-the-loop controls (meaning you stay 100% in charge of every entry and sign-off), but it does the 4-hour daily bank matching dog-work in under 50 milliseconds.

You can forward this directly to ${dealerPrincipalName} (${dealerPrincipalTitle}) or use it to show them how you’re proactively streamlining the dealership's financial ops without adding permanent payroll overhead.

Let me know what you think!

Much love,
Raksha`;

  const handleCopyProposal = () => {
    navigator.clipboard.writeText(plainTextProposal);
    setCopiedProposal(true);
    if (showToast) showToast('📋 Dealership Pilot Proposal copied to clipboard!', 'success');
    setTimeout(() => setCopiedProposal(false), 3000);
  };

  const handleCopyCodeBlock = () => {
    navigator.clipboard.writeText(plainTextProposal);
    setCopiedCodeBlock(true);
    if (showToast) showToast('📋 Proposal Text Code Block copied to clipboard!', 'success');
    setTimeout(() => setCopiedCodeBlock(false), 3000);
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([plainTextProposal], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AzuraOS_Dealership_Pilot_Proposal_${dealershipName.replace(/[^a-zA-Z0-9]/g, '_')}_${pilotRef}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    if (showToast) showToast('📥 Proposal text file downloaded!', 'success');
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(sisterEmailDraft);
    setCopiedEmail(true);
    if (showToast) showToast('✉️ Sister Email Draft copied to clipboard!', 'success');
    setTimeout(() => setCopiedEmail(false), 3000);
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const exportElement = document.getElementById('dealership-proposal-a4-canvas');
      if (!exportElement) {
        throw new Error('Proposal canvas element not found');
      }

      // Render at fixed standard desktop A4 dimensions (794px x 1123px) so mobile viewport never compresses or distorts it
      const canvas = await html2canvas(exportElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: documentTheme === 'dark' ? '#0B0E0F' : '#FFFFFF',
        logging: false,
        windowWidth: 1024,
        onclone: (clonedDoc) => {
          sanitizeClonedDocForPdf(clonedDoc);
          const clonedCanvas = clonedDoc.getElementById('dealership-proposal-a4-canvas');
          if (clonedCanvas) {
            clonedCanvas.style.width = '794px';
            clonedCanvas.style.minWidth = '794px';
            clonedCanvas.style.maxWidth = '794px';
            clonedCanvas.style.boxSizing = 'border-box';
            clonedCanvas.style.padding = '32px 36px';
            clonedCanvas.style.margin = '0 auto';
            clonedCanvas.style.backgroundColor = documentTheme === 'dark' ? '#0B0E0F' : '#FFFFFF';
            clonedCanvas.style.color = documentTheme === 'dark' ? '#E2E8F0' : '#0F172A';
            clonedCanvas.style.boxShadow = 'none';
            clonedCanvas.style.border = 'none';
            clonedCanvas.style.borderRadius = '0';
          }
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      const pdfWidth = 210; // A4 standard width (mm)
      const pdfHeight = 297; // A4 standard height (mm)

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      pdf.save(`AzuraOS_Eagle_Ford_14Day_Pilot_Proposal_${new Date().toISOString().slice(0, 10)}.pdf`);

      if (showToast) showToast('📥 Official 1-Page A4 Proposal PDF downloaded!', 'success');
    } catch (err) {
      console.error('Error generating dealership proposal PDF:', err);
      if (showToast) showToast('Failed to generate PDF. You can use Copy Text or Print as fallback.', 'error');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 text-left bg-[#0C0E0F] text-gray-200" style={{ backgroundColor: '#0C0E0F' }}>
      {/* 1. TOP HEADER & ACTION BAR */}
      <div className="p-5 sm:p-6 bg-[#111518] border border-emerald-500/30 rounded-3xl shadow-xl relative overflow-hidden space-y-5">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-5 border-b border-[#242C30] pb-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center shrink-0 shadow-lg">
              <Car size={26} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/25">
                  CONFIDENTIAL B2B PROPOSAL &amp; SLA
                </span>
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-500/25 flex items-center gap-1">
                  <ShieldCheck size={11} /> SUPER ADMIN EXCLUSIVE
                </span>
                <span className="text-[10px] font-mono text-gray-400">
                  REF: {pilotRef}
                </span>
              </div>
              <h2 className="text-lg sm:text-2xl font-bold font-display text-white tracking-tight">
                {dealershipName} — 14-Day Reconciliation &amp; SARS VAT Pilot SLA
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 max-w-3xl leading-relaxed">
                Prepared for <strong>{financialManagerName}</strong> ({financialManagerTitle}) and <strong>{dealerPrincipalName}</strong> ({dealerPrincipalTitle}). Formatted as a complete 1-page executive A4 agreement ready for instant PDF export and email dispatch.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <button
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
              title="Download clean, unbroken 1-page A4 PDF"
            >
              {isGeneratingPdf ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
              <span>{isGeneratingPdf ? 'Generating PDF...' : 'Download Official A4 PDF'}</span>
            </button>

            <button
              onClick={handleCopyCodeBlock}
              className="px-4 py-2.5 bg-[#171D20] hover:bg-[#20272B] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2D363B] transition flex items-center gap-2 cursor-pointer"
            >
              {copiedCodeBlock ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-emerald-400" />}
              <span>{copiedCodeBlock ? 'Copied Code Block!' : 'Copy Code Block'}</span>
            </button>

            <button
              onClick={handleCopyEmail}
              className="px-4 py-2.5 bg-[#171D20] hover:bg-[#20272B] text-gray-200 hover:text-white font-bold text-xs rounded-xl border border-[#2D363B] transition flex items-center gap-2 cursor-pointer"
            >
              {copiedEmail ? <Check size={14} className="text-amber-400" /> : <Mail size={14} className="text-amber-400" />}
              <span>Copy Sister Email</span>
            </button>

            <button
              type="button"
              onClick={() => setDocumentTheme(prev => prev === 'dark' ? 'light' : 'dark')}
              className={`px-3.5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer border ${
                documentTheme === 'light'
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-[#151819] border-[#2A2F31] text-gray-300 hover:text-white'
              }`}
              title="Toggle between corporate white print view and dark vault view"
            >
              <span>{documentTheme === 'light' ? '☀️ Print White Theme' : '🌙 Dark Vault Theme'}</span>
            </button>
          </div>
        </div>

        {/* 2. SUB-TABS SELECTOR */}
        <div className="pt-1 flex items-center gap-2 overflow-x-auto scrollbar-none pb-1 border-t border-[#222A2E]">
          {[
            { id: 'preview', label: '🚗 1. Complete A4 Document Preview', icon: FileText },
            { id: 'code_block', label: '📋 2. Raw Text Code Block (For Boardy/Sister)', icon: Copy },
            { id: 'email', label: '✉️ 3. Sister Email Pitch Draft', icon: Mail },
            { id: 'customize', label: '⚙️ 4. Customize & Save Parameters', icon: Sliders },
          ].map((tab) => {
            const Icon = tab.icon;
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer shrink-0 ${
                  isSelected
                    ? 'bg-emerald-500 text-black shadow-md shadow-emerald-500/20 font-extrabold'
                    : 'bg-[#151819] text-gray-400 hover:text-white hover:bg-[#1F2426]'
                }`}
              >
                <Icon size={14} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* 3. CUSTOMIZE PROPOSAL PANEL */}
        {activeTab === 'customize' && (
          <div className="pt-4 border-t border-[#222A2E] space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Sliders size={16} className="text-emerald-400" />
                <span>Customize &amp; Persist Dealership Parameters</span>
              </h3>
              <span className="text-[11px] text-emerald-400 font-mono flex items-center gap-1">
                <CheckCircle size={13} /> Auto-saved to local vault
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 text-xs">
              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Dealership Company Name</label>
                <input
                  type="text"
                  value={dealershipName}
                  onChange={(e) => setDealershipName(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Financial Manager Name</label>
                <input
                  type="text"
                  value={financialManagerName}
                  onChange={(e) => setFinancialManagerName(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Dealer Principal Name</label>
                <input
                  type="text"
                  value={dealerPrincipalName}
                  onChange={(e) => setDealerPrincipalName(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Provider Legal Entity</label>
                <input
                  type="text"
                  value={companyProvider}
                  onChange={(e) => setCompanyProvider(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Company Registration Number</label>
                <input
                  type="text"
                  value={companyRegNumber}
                  onChange={(e) => setCompanyRegNumber(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Location / City</label>
                <input
                  type="text"
                  value={cityLocation}
                  onChange={(e) => setCityLocation(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Target Operating Banks</label>
                <input
                  type="text"
                  value={banksList}
                  onChange={(e) => setBanksList(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Vehicle Finance Houses</label>
                <input
                  type="text"
                  value={financeHouses}
                  onChange={(e) => setFinanceHouses(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>

              <div>
                <label className="text-[10px] font-mono text-gray-400 font-bold uppercase">Assistant Salary Replaced</label>
                <input
                  type="text"
                  value={salarySavings}
                  onChange={(e) => setSalarySavings(e.target.value)}
                  className="w-full mt-1 bg-[#15191C] border border-[#2A343A] rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-emerald-500/60"
                />
              </div>
            </div>
            
            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setActiveTab('preview')}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-xs rounded-xl transition cursor-pointer"
              >
                View Live Proposal Document →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 4. TAB 2: RAW TEXT CODE BLOCK VIEW */}
      {activeTab === 'code_block' && (
        <div className="p-6 bg-[#0E1214] border border-[#242C30] rounded-3xl space-y-4 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2528] pb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center justify-center font-mono text-xs font-bold">
                TXT
              </div>
              <div>
                <h3 className="text-sm font-bold text-white tracking-tight">{dealershipName} Pilot Proposal — Raw Text Code Block</h3>
                <p className="text-[11px] text-gray-400">Copy and paste directly to Boardy, email, or your sister for zero-friction review</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyCodeBlock}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-md"
              >
                {copiedCodeBlock ? <Check size={14} /> : <Copy size={14} />}
                <span>{copiedCodeBlock ? 'Copied Code Block!' : 'Copy Code Block'}</span>
              </button>
              <button
                onClick={handleDownloadTxt}
                className="px-3.5 py-2 bg-[#161B1D] hover:bg-[#20272B] text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#263137] transition flex items-center gap-1.5 cursor-pointer"
              >
                <Download size={14} />
                <span>Download .txt</span>
              </button>
            </div>
          </div>

          <div className="relative">
            <pre className="p-5 bg-[#07090A] border border-[#1C2327] rounded-2xl text-xs font-mono text-emerald-300/90 leading-relaxed overflow-x-auto selection:bg-emerald-500/20 max-h-[700px] custom-scrollbar whitespace-pre-wrap">
              {plainTextProposal}
            </pre>
          </div>
        </div>
      )}

      {/* 5. TAB 3: SISTER EMAIL PITCH DRAFT VIEW */}
      {activeTab === 'email' && (
        <div className="p-6 bg-[#0E1214] border border-[#242C30] rounded-3xl space-y-4 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1F2528] pb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center justify-center shrink-0">
                <Mail size={18} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white tracking-tight">Sister Email Pitch Draft (Ready to Send to {financialManagerName})</h3>
                <p className="text-[11px] text-gray-400">Positioned as an internal tool to help her stop doing 4 hours of manual recon daily</p>
              </div>
            </div>
            <button
              onClick={handleCopyEmail}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-md"
            >
              {copiedEmail ? <Check size={14} /> : <Copy size={14} />}
              <span>{copiedEmail ? 'Copied Email Draft!' : 'Copy Email Pitch'}</span>
            </button>
          </div>

          <div className="p-5 bg-[#07090A] border border-[#1C2327] rounded-2xl space-y-3 text-xs leading-relaxed">
            <div className="text-gray-400 font-mono text-[11px] border-b border-[#1C2327] pb-2">
              <strong className="text-gray-200">Subject:</strong> Quick thought on your assistant accountant search + a 14-day {dealershipName} pilot proposal
            </div>
            <div className="text-gray-200 whitespace-pre-wrap font-sans text-xs leading-relaxed pt-1">
              {sisterEmailDraft.split('\n\n').slice(1).join('\n\n')}
            </div>
          </div>
        </div>
      )}

      {/* 6. TAB 1: PROPOSAL DOCUMENT PREVIEW (PRINT / PDF CANVAS - FIXED 794px A4 DESKTOP SHEET) */}
      {activeTab === 'preview' && (
      <div className="flex justify-center overflow-x-auto py-2">
        <div 
          id="dealership-proposal-a4-canvas" 
          ref={proposalDocRef}
          className={`w-full max-w-[794px] border rounded-2xl p-6 sm:p-8 shadow-2xl space-y-4 font-sans transition-colors shrink-0 ${
            documentTheme === 'dark'
              ? 'bg-[#0B0E0F] text-gray-200 border-[#263137]'
              : 'bg-white text-gray-900 border-gray-300'
          }`}
          style={{
            width: '794px',
            minHeight: '1080px',
            backgroundColor: documentTheme === 'dark' ? '#0B0E0F' : '#FFFFFF',
            color: documentTheme === 'dark' ? '#E2E8F0' : '#0F172A',
            boxSizing: 'border-box'
          }}
        >
          {/* DOCUMENT HEADER */}
          <div className="flex items-start justify-between gap-4 border-b border-[#21292D] pb-3.5">
            <div className="space-y-1">
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold font-mono text-xs shadow ${
                  documentTheme === 'dark'
                    ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                    : 'bg-emerald-600 text-white'
                }`}>
                  AZ
                </div>
                <div>
                  <h1 className={`text-base font-bold font-display tracking-tight leading-tight ${
                    documentTheme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    AZURAOS™ MOTOR DEALERSHIP PILOT SLA
                  </h1>
                  <p className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
                    Automated Bank Reconciliation &amp; SARS VAT Margin Defense Engine
                  </p>
                </div>
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 space-y-0.5 pt-0.5">
                <p><strong>Provider:</strong> {companyProvider} (Registration Number: {companyRegNumber})</p>
                <p><strong>Lead Architect:</strong> Raksha Singh • founder@azuraos.app</p>
                <p className="flex items-center gap-1 text-emerald-600 dark:text-emerald-300 font-mono text-[10px]">
                  <ShieldCheck size={12} className="text-emerald-500" />
                  <span>Google Cloud Partner Advantage Member • Air-Gapped Zero-Custody</span>
                </p>
              </div>
            </div>

            <div className={`p-2.5 rounded-xl text-xs space-y-0.5 text-right shrink-0 border ${
              documentTheme === 'dark'
                ? 'bg-[#121618] border-[#263137]'
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400 uppercase font-bold">Proposal Reference</div>
              <div className={`font-mono text-xs font-bold ${documentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{pilotRef}</div>
              <div className="text-[10px] text-gray-500">
                {new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="inline-block mt-0.5 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-300 rounded font-mono text-[9px] font-bold">
                {pilotDays} (R0 TRIAL)
              </div>
            </div>
          </div>

          {/* TARGET RECIPIENT CARD */}
          <div className={`p-3 rounded-xl text-xs space-y-0.5 border ${
            documentTheme === 'dark'
              ? 'bg-[#101416] border-[#232B2F]'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <span className="text-[9px] font-mono text-gray-500 uppercase font-bold">Target Dealership &amp; Executive Leadership:</span>
            <div className={`text-sm font-bold ${documentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{dealershipName}</div>
            <div className="text-gray-600 dark:text-gray-300 text-[11px]">
              <strong>{financialManagerName}</strong> ({financialManagerTitle}) &amp; <strong>{dealerPrincipalName}</strong> ({dealerPrincipalTitle}) • {cityLocation}
            </div>
            <div className="text-[10px] text-gray-500 font-mono">
              Operating Banks: <span className={documentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>{banksList}</span> | Vehicle Finance: <span className={documentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>{financeHouses}</span>
            </div>
          </div>

          {/* SECTION 1: THE OPERATIONAL BOTTLENECK */}
          <div className="space-y-1.5">
            <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 border-b pb-1 ${
              documentTheme === 'dark' ? 'text-white border-[#21292D]' : 'text-gray-900 border-gray-200'
            }`}>
              <span className="w-4 h-4 rounded-md bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-[10px]">1</span>
              <span>The Operational Bottleneck in Dealership Accounting</span>
            </h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#111618] border-[#222B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <strong className="text-emerald-600 dark:text-emerald-300 text-[11px] flex items-center gap-1">
                  <CheckCircle size={11} className="text-emerald-500" /> Multi-Source Deposit Chaos
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Matching buyer cash, EFT deposits, and delayed bank vehicle finance payouts ({financeHouses}) to specific vehicle stock reference numbers.
                </p>
              </div>
              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#111618] border-[#222B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <strong className="text-emerald-600 dark:text-emerald-300 text-[11px] flex items-center gap-1">
                  <CheckCircle size={11} className="text-emerald-500" /> SARS VAT &amp; Floorplan Interest
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Calculating moving 15% VAT on second-hand vehicle gross margins ahead of bi-monthly VAT201 deadlines while defending floorplan capital.
                </p>
              </div>
              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#111618] border-[#222B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <strong className="text-rose-600 dark:text-rose-300 text-[11px] flex items-center gap-1">
                  <Lock size={11} className="text-rose-500" /> Junior Assistant Headcount Drag
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Hiring an assistant accountant costs <strong>{salarySavings}</strong> plus recruitment fees, frequent turnover, and ongoing supervisor overhead.
                </p>
              </div>
              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#111618] border-[#222B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <strong className="text-amber-600 dark:text-amber-300 text-[11px] flex items-center gap-1">
                  <ShieldCheck size={11} className="text-amber-500" /> The Anti-Plaid Privacy Guarantee
                </strong>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Dealerships cannot risk sharing online banking passwords. AzuraOS runs 100% locally in browser memory with zero password access.
                </p>
              </div>
            </div>
          </div>

          {/* SECTION 2: THE 4 CORE DELIVERABLES */}
          <div className="space-y-1.5">
            <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 border-b pb-1 ${
              documentTheme === 'dark' ? 'text-white border-[#21292D]' : 'text-gray-900 border-gray-200'
            }`}>
              <span className="w-4 h-4 rounded-md bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-[10px]">2</span>
              <span>14-Day Pilot Architecture &amp; Core Deliverables</span>
            </h3>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#121618] border-[#232B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between gap-1">
                  <div className={`font-bold text-[11px] flex items-center gap-1 ${
                    documentTheme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <span className="text-emerald-600 dark:text-emerald-400 font-mono">A.</span>
                    <span>0.05s Bank Reconciler</span>
                  </div>
                  <span className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-500/10 px-1 py-0.5 rounded">
                    R0 INCLUDED
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Drag-and-drop daily {banksList} statements. Auto-matches vehicle stock numbers and bank asset payouts in 50ms.
                </p>
              </div>

              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#121618] border-[#232B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between gap-1">
                  <div className={`font-bold text-[11px] flex items-center gap-1 ${
                    documentTheme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <span className="text-emerald-600 dark:text-emerald-400 font-mono">B.</span>
                    <span>1-Click Human Sign-Off</span>
                  </div>
                  <span className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-500/10 px-1 py-0.5 rounded">
                    R0 INCLUDED
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  No blind posting. {financialManagerName} retains 100% sign-off control, reviewing and approving line items with 1 click.
                </p>
              </div>

              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#121618] border-[#232B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between gap-1">
                  <div className={`font-bold text-[11px] flex items-center gap-1 ${
                    documentTheme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <span className="text-emerald-600 dark:text-emerald-400 font-mono">C.</span>
                    <span>SARS 15% VAT Shield</span>
                  </div>
                  <span className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-500/10 px-1 py-0.5 rounded">
                    R0 INCLUDED
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Calculates 15% VAT holding buffers on second-hand vehicle margins, protecting {dealershipName} from VAT201 shortfalls.
                </p>
              </div>

              <div className={`p-2 rounded-lg space-y-0.5 border ${
                documentTheme === 'dark' ? 'bg-[#121618] border-[#232B2F]' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between gap-1">
                  <div className={`font-bold text-[11px] flex items-center gap-1 ${
                    documentTheme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <span className="text-emerald-600 dark:text-emerald-400 font-mono">D.</span>
                    <span>Safe Draw Radar</span>
                  </div>
                  <span className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-500/10 px-1 py-0.5 rounded">
                    R0 INCLUDED
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-[10px] leading-tight">
                  Delivers {dealerPrincipalName} real-time visibility into true safe-to-spend cash after subtracting floorplan interest &amp; buffers.
                </p>
              </div>
            </div>
          </div>

          {/* SECTION 3: COMMERCIAL VALUE & HEADCOUNT COMPARISON */}
          <div className={`p-2.5 rounded-xl space-y-1.5 border ${
            documentTheme === 'dark'
              ? 'bg-gradient-to-r from-emerald-500/10 via-[#131719] to-emerald-500/5 border-emerald-500/30'
              : 'bg-emerald-50/50 border-emerald-200'
          }`}>
            <h4 className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider font-mono">
              3. Commercial Economics &amp; Headcount Arbitrage
            </h4>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className={`p-1.5 rounded-lg border ${
                documentTheme === 'dark' ? 'bg-[#0E1214] border-[#232B2F]' : 'bg-white border-gray-200'
              }`}>
                <div className="text-[8px] font-mono text-gray-500 uppercase">Assistant Accountant Cost</div>
                <div className="text-xs font-bold text-rose-600 dark:text-rose-400 mt-0.5">{salarySavings}</div>
                <div className="text-[8px] text-gray-500 mt-0.5">Plus recruitment overhead</div>
              </div>
              <div className={`p-1.5 rounded-lg border ${
                documentTheme === 'dark' ? 'bg-[#0E1214] border-emerald-500/40' : 'bg-white border-emerald-300'
              }`}>
                <div className="text-[8px] font-mono text-emerald-600 dark:text-emerald-400 uppercase font-bold">Post-Pilot Retainer</div>
                <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300 mt-0.5">{postPilotFee}</div>
                <div className="text-[8px] text-emerald-600 dark:text-emerald-400/80 mt-0.5">100% software leverage</div>
              </div>
              <div className={`p-1.5 rounded-lg border ${
                documentTheme === 'dark' ? 'bg-[#0E1214] border-[#232B2F]' : 'bg-white border-gray-200'
              }`}>
                <div className="text-[8px] font-mono text-amber-600 dark:text-amber-400 uppercase font-bold">Net Monthly Saved</div>
                <div className="text-xs font-bold text-amber-600 dark:text-amber-300 mt-0.5">R16,500 – R30,000 / mo</div>
                <div className="text-[8px] text-gray-500 mt-0.5">R198k – R360k/yr bottom line</div>
              </div>
            </div>
          </div>

          {/* SECTION 4: AUTHORIZED SIGN-OFF COVENANT */}
          <div className="pt-2 border-t border-[#21292D] space-y-2">
            <div className="text-[9px] text-gray-500 dark:text-gray-400 leading-snug">
              <strong>Non-Disclosure &amp; Zero-Custody Covenant:</strong> {companyProvider} (Registration Number: {companyRegNumber}) provides a zero-custody guarantee. Bank statements and vehicle transaction records remain resident in local browser memory and are never transferred, stored on cloud servers, or used for AI training.
            </div>

            <div className="grid grid-cols-2 gap-3 pt-0.5 text-xs">
              <div className={`p-2.5 rounded-xl space-y-1.5 border ${
                documentTheme === 'dark' ? 'bg-[#111517] border-[#263137]' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="text-[9px] font-mono text-gray-500 uppercase font-bold">Dealership Authorized Approval:</div>
                <div className="h-6 border-b border-dashed border-gray-400 dark:border-[#3A454B] flex items-end pb-0.5 text-gray-400 text-[9px]">
                  Authorized Executive Sign-Off
                </div>
                <div className="space-y-0.5 text-[10px] leading-tight pt-0.5">
                  <p className={`font-semibold ${documentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {dealerPrincipalName}, {dealerPrincipalTitle}
                  </p>
                  <p className={`font-semibold ${documentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {financialManagerName}, {financialManagerTitle}
                  </p>
                  <p className="text-gray-500">{dealershipName}</p>
                </div>
              </div>

              <div className={`p-2.5 rounded-xl space-y-1.5 border ${
                documentTheme === 'dark' ? 'bg-[#111517] border-[#263137]' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400 uppercase font-bold">
                  AzuraOS Principal Architect:
                </div>
                <div className="h-6 border-b border-dashed border-emerald-500/50 flex items-end pb-0.5 text-emerald-600 dark:text-emerald-400 font-mono text-[9px]">
                  Raksha Singh (Verified Electronic Seal)
                </div>
                <div className="space-y-0.5 text-[10px] leading-tight pt-0.5">
                  <p className={`font-semibold ${documentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Raksha Singh, Principal Architect
                  </p>
                  <p className="text-gray-500">{companyProvider} (Reg: {companyRegNumber})</p>
                  <p className="text-gray-500 text-[8px]">Google Cloud Partner Advantage Member</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
